import { useEffect, useRef, useState } from "react";
import { useApp } from "@/store/AppStore";

const FONTS = ["Sora", "Inter", "Pacifico", "Georgia", "Arial"];
const SWATCHES = ["#FFFFFF", "#00A3E0", "#7FD8F7", "#C9D6EE", "#FFD166", "#F4978E"];

// Control compacto de estilo de títulos (fuente, color y glow) para editar
// directamente sobre cada encabezado de las pantallas.
const TitleStyleInline = () => {
  const { data, updateSettings } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const style = data.settings.titleStyle;

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const patch = (p: Partial<typeof style>) => updateSettings({ titleStyle: { ...style, ...p } });

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-300 hover:bg-background-200/60 cursor-pointer"
        aria-label="Estilo del título"
        title="Fuente, color y resplandor del título"
      >
        <i className="ri-font-size-2 text-sm" />
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-[260px] glass-panel-strong rounded-lg p-3.5 z-50 animate-slide-down text-left">
          <p className="text-xs font-semibold text-foreground-950 mb-2">Estilo de este título</p>

          <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Fuente</label>
          <div className="grid grid-cols-2 gap-1.5 mb-3">
            {FONTS.map((f) => (
              <button
                key={f}
                onClick={() => patch({ font: f })}
                style={{ fontFamily: f }}
                className={`px-2 py-1.5 rounded-md text-xs border cursor-pointer ${
                  style.font === f
                    ? "bg-primary-500/25 border-accent-500/40 text-foreground-950"
                    : "border-background-300/50 text-foreground-600 hover:border-accent-500/30"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Color</label>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="color"
              value={style.color}
              onChange={(e) => patch({ color: e.target.value })}
              className="w-8 h-7 rounded-md bg-transparent border border-background-300/50 cursor-pointer"
              aria-label="Color de título"
            />
            <div className="flex gap-1.5 flex-wrap">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  onClick={() => patch({ color: c })}
                  style={{ background: c }}
                  className={`w-5 h-5 rounded-full border cursor-pointer ${
                    style.color.toLowerCase() === c.toLowerCase()
                      ? "border-accent-400 ring-2 ring-accent-500/40"
                      : "border-background-300/60"
                  }`}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-[10px] font-semibold text-foreground-600">Resplandor (glow)</span>
            <button
              onClick={() => patch({ glow: !style.glow })}
              className={`w-10 h-5 rounded-full transition-colors cursor-pointer ${
                style.glow ? "bg-accent-500" : "bg-background-300/60"
              }`}
              aria-label="Alternar resplandor"
            >
              <span
                className={`block w-4 h-4 rounded-full bg-background-50 transition-transform ${
                  style.glow ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </label>
        </div>
      )}
    </div>
  );
};

export default TitleStyleInline;